import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { listGroup } from "../../Redux/Actions/ProductActions";
import Loading from "../LoadingError/Loading";
import Message from "../LoadingError/Error";
import { Link } from "react-router-dom";
import { IMAGE_CACHE_URL_GROUP } from "../../Redux/Constants/GlobalConstant";

const HOVER_MAX = 4;
const DEAD_ZONE = 0.15;

const CategorySection = () => {
  const dispatch = useDispatch();
  const { loading, error, groups } = useSelector((s) => s.groupList);

  const scrollRef   = useRef(null);
  const animRef     = useRef(null);
  const posRef      = useRef(0);
  const hoveringRef = useRef(false);
  const hoverSpeed  = useRef(0);

  const dragging    = useRef(false);
  const startX      = useRef(0);
  const startScroll = useRef(0);
  const didDrag     = useRef(false);

  useEffect(() => { dispatch(listGroup()); }, [dispatch]);

  // ── Animation tick: auto-scroll when no hover, hover-speed when hovering ──
  const tick = useCallback(() => {
    const el = scrollRef.current;
    if (el && !dragging.current && hoveringRef.current) {
      posRef.current += hoverSpeed.current;
      const max = el.scrollWidth - el.clientWidth;
      if (posRef.current > max) posRef.current = max;
      if (posRef.current < 0)   posRef.current = 0;
      el.scrollLeft = posRef.current;
    }
    animRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (!loading && !error && groups?.length > 0) {
      animRef.current = requestAnimationFrame(tick);
    }
    return () => cancelAnimationFrame(animRef.current);
  }, [loading, error, groups, tick]);

  // ── Hover → speed ────────────────────────────────────────────────────────
  const onMouseMove = (e) => {
    const el = scrollRef.current;
    if (!el || dragging.current) return;
    const { left, width } = el.getBoundingClientRect();
    const rel      = (e.clientX - left) / width;
    const centered = rel - 0.5;
    if (Math.abs(centered) < DEAD_ZONE) {
      hoverSpeed.current = 0;
    } else {
      const factor = (Math.abs(centered) - DEAD_ZONE) / (0.5 - DEAD_ZONE);
      hoverSpeed.current = Math.sign(centered) * factor * HOVER_MAX;
    }
  };

  const onMouseEnter = () => { hoveringRef.current = true; };
  const onMouseLeave = () => { hoveringRef.current = false; hoverSpeed.current = 0; };

  // ── Drag ─────────────────────────────────────────────────────────────────
  const onMouseDown = (e) => {
    if (e.button !== 0 || !scrollRef.current) return;
    e.preventDefault();
    dragging.current    = true;
    didDrag.current     = false;
    startX.current      = e.clientX;
    startScroll.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = "grabbing";
  };

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current || !scrollRef.current) return;
      const dx = e.clientX - startX.current;
      if (Math.abs(dx) > 4) didDrag.current = true;
      const next = startScroll.current - dx;
      scrollRef.current.scrollLeft = next;
      posRef.current = next;
    };
    const up = () => {
      if (!dragging.current) return;
      dragging.current = false;
      if (scrollRef.current) scrollRef.current.style.cursor = "default";
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, []);

  // ── Touch drag (mobile) ───────────────────────────────────────────────────
  const onTouchStart = (e) => {
    hoveringRef.current = false;
    dragging.current    = true;
    didDrag.current     = false;
    startX.current      = e.touches[0].clientX;
    startScroll.current = scrollRef.current.scrollLeft;
  };

  const onTouchMove = (e) => {
    if (!dragging.current || !scrollRef.current) return;
    const dx = e.touches[0].clientX - startX.current;
    if (Math.abs(dx) > 4) didDrag.current = true;
    const next = startScroll.current - dx;
    scrollRef.current.scrollLeft = next;
    posRef.current = next;
  };

  const onTouchEnd = () => { dragging.current = false; };

  const guardClick = (e) => { if (didDrag.current) e.preventDefault(); };

  return (
    <div className="container">
      {loading ? <div className="mb-5"><Loading /></div>
        : error ? <Message variant="alert-danger">{error}</Message>
        : (
          <div className="section">
            <div className="d-flex align-items-center" style={{ justifyContent: "space-between" }}>
              <h5>Shop by Category</h5>
              <Link to={groups && groups.length > 0 ? `/group/${groups[0]._id}` : "#"} className="view-all-btn" style={{ position: "relative", zIndex: 10 }}>
                View All
              </Link>
            </div>

            <div
              className="scrollmenu"
              ref={scrollRef}
              onMouseMove={onMouseMove}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              onMouseDown={onMouseDown}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              style={{ userSelect: "none" }}
            >
              {groups?.map((group, i) => (
                <Link to={`/group/${group._id}`} key={i} onClick={guardClick}>
                  <div className="cat-section-thumb-img">
                    <img
                      src={IMAGE_CACHE_URL_GROUP + "ag_group_icon/" + group.group_img}
                      alt={group?.group_img}
                      draggable="false"
                      style={{ pointerEvents: "none" }}
                    />
                    <span>{group.group_name}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )
      }
    </div>
  );
};

export default CategorySection;
